// The left rail: the whole library at a glance, one row per video.
//
// Search matches identifier, category and products as well as the title. The
// old admin filtered on title alone, which meant an untitled video could not
// be found at all once the list grew past a screen.

import React, { useMemo, useRef } from 'react';
import { Box, Button, InputAdornment, TextField, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useAdmin } from './AdminStore';
import { deriveStatus } from './schema';
import { parseProducts } from '../ps/format';
import StatusLeds from './StatusLeds';

// "To do" is the worklist and reads the manual marker, not the checklist: a
// video leaves it when you say it is done, not when a heuristic agrees.
const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'todo', label: 'To do' },
  { key: 'live', label: 'Live' },
  { key: 'hidden', label: 'Hidden' },
];

function matchesSearch(identifier, video, needle) {
  if (!needle) return true;
  const haystack = [
    identifier,
    video.title,
    video.category,
    ...parseProducts(video.products),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(needle);
}

export function VideoRail({ search, onSearch, filter, onFilter, searchRef }) {
  const { videos, selectedId, select } = useAdmin();
  const listRef = useRef(null);

  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return Object.entries(videos)
      .filter(([identifier, video]) => {
        if (!matchesSearch(identifier, video, needle)) return false;
        const status = deriveStatus(video);
        if (filter === 'live') return status.live;
        if (filter === 'hidden') return !status.live;
        if (filter === 'todo') return !status.finished;
        return true;
      })
      .sort((a, b) => (a[1].title || a[0]).localeCompare(b[1].title || b[0]));
  }, [videos, search, filter]);

  // Arrow keys move the selection without leaving the search box, so finding a
  // video is one gesture: type, arrow down, edit.
  const onKeyDown = (event) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    if (!rows.length) return;
    event.preventDefault();
    const index = rows.findIndex(([id]) => id === selectedId);
    const next =
      event.key === 'ArrowDown'
        ? Math.min(rows.length - 1, index + 1)
        : Math.max(0, index - 1);
    select(rows[next === -1 ? 0 : next][0]);
  };

  return (
    <aside className="admin-rail" onKeyDown={onKeyDown}>
      <Box sx={{ p: '10px', borderBottom: '1px solid var(--ps-line-subtle)' }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search  /"
          value={search}
          inputRef={searchRef}
          onChange={(e) => onSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16, color: 'var(--ps-text-dim)' }} />
              </InputAdornment>
            ),
          }}
        />
        <Box sx={{ display: 'flex', gap: '4px', mt: '10px' }}>
          {FILTERS.map(({ key, label }) => (
            <Button
              key={key}
              size="small"
              variant={filter === key ? 'contained' : 'outlined'}
              onClick={() => onFilter(key)}
              sx={{ flex: 1, px: 0 }}
            >
              {label}
            </Button>
          ))}
        </Box>
      </Box>

      <div className="admin-rail__list" ref={listRef}>
        {rows.map(([identifier, video]) => (
          <button
            type="button"
            key={identifier}
            className={`admin-row${identifier === selectedId ? ' is-selected' : ''}`}
            onClick={() => select(identifier)}
          >
            {video.minithumbnailUrl || video.thumbnailUrl ? (
              <img
                className="admin-row__thumb"
                src={video.minithumbnailUrl || video.thumbnailUrl}
                alt=""
                loading="lazy"
              />
            ) : (
              <span className="admin-row__thumb" />
            )}
            <span style={{ minWidth: 0 }}>
              <span className="admin-row__title">{video.title || identifier}</span>
              <span className="admin-row__meta">
                <StatusLeds video={video} />
                {deriveStatus(video).finished && (
                  <span className="admin-row__done" title="Marked finished">✓</span>
                )}
                <span className="admin-row__category">{video.category || 'no category'}</span>
              </span>
            </span>
          </button>
        ))}

        {!rows.length && (
          <Typography variant="body2" sx={{ p: '10px', color: 'var(--ps-text-dim)' }}>
            {Object.keys(videos).length ? 'Nothing matches.' : 'No videos yet.'}
          </Typography>
        )}
      </div>

      <Box
        sx={{
          p: '6px 10px',
          borderTop: '1px solid var(--ps-line-subtle)',
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <span className="admin-label">{rows.length} shown</span>
        <span className="admin-label">{Object.keys(videos).length} total</span>
      </Box>
    </aside>
  );
}

export default VideoRail;
